import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AppointmentPicker = ({ value, onChange, refreshSignal = 0 }) => {
  const [days, setDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSlots = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/appointments/slots`, { params: { days: 14 } });
      const nextDays = response.data.days || [];
      setDays(nextDays);
      const firstAvailable = nextDays.find((day) => day.slots.some((slot) => slot.available));
      setSelectedDate((current) => (nextDays.some((day) => day.date === current) ? current : firstAvailable?.date || ""));
    } catch (err) {
      setError("Appointment availability could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSlots();
  }, [refreshSignal, loadSlots]);

  const activeDay = useMemo(() => days.find((day) => day.date === selectedDate) || days[0], [days, selectedDate]);

  if (loading) {
    return (
      <div className="flex min-h-40 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04]" data-testid="appointment-slots-loading">
        <Loader2 className="h-5 w-5 animate-spin text-brand-copper" />
        <span className="ml-3 text-sm text-white/70">Loading available consultation times…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-300/20 bg-red-500/10 p-5" data-testid="appointment-slots-error">
        <p className="text-sm font-bold text-red-100">{error}</p>
        <Button type="button" variant="outline" onClick={loadSlots} data-testid="appointment-slots-retry-button" className="mt-4 rounded-full border-white/20 bg-transparent text-white hover:bg-white hover:text-brand-navy">
          <RefreshCw className="h-4 w-4" /> Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 rounded-lg border border-white/10 bg-white/[0.04] p-4 sm:p-5" data-testid="appointment-picker">
      <div className="mb-4 flex items-center gap-3">
        <CalendarDays className="h-5 w-5 text-brand-copper" />
        <div>
          <p className="text-sm font-extrabold text-white">Choose an available time</p>
          <p className="text-xs text-white/52">30-minute consultations · Pacific Time</p>
        </div>
      </div>
      <div className="-mx-1 flex w-full min-w-0 gap-2 overflow-x-auto px-1 pb-2" role="list" aria-label="Available appointment days">
        {days.map((day) => {
          const hasAvailability = day.slots.some((slot) => slot.available);
          const active = activeDay?.date === day.date;
          return (
            <button
              key={day.date}
              type="button"
              disabled={!hasAvailability}
              onClick={() => setSelectedDate(day.date)}
              data-testid={`appointment-day-${day.date}`}
              className={`min-h-14 min-w-24 shrink-0 rounded-lg border px-4 py-2 text-left transition-colors duration-300 ${
                active ? "border-brand-copper bg-brand-copper text-brand-navy" : "border-white/12 bg-slate-800 text-white hover:border-brand-copper/70"
              } ${!hasAvailability ? "cursor-not-allowed opacity-35" : ""}`}
            >
              <span className="block text-[11px] font-bold uppercase tracking-[0.14em]">{day.weekday.slice(0, 3)}</span>
              <span className="mt-1 block text-sm font-extrabold">{day.label}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3" aria-label="Available appointment times">
        {activeDay?.slots.map((slot, index) => {
          const selected = value === slot.start;
          return (
            <button
              key={slot.start}
              type="button"
              disabled={!slot.available}
              onClick={() => onChange(slot.start)}
              data-testid={`appointment-time-${activeDay.date}-${index}`}
              aria-pressed={selected}
              className={`min-h-12 rounded-lg border px-3 text-sm font-extrabold transition-colors duration-300 ${
                selected ? "border-brand-copper bg-brand-copper text-brand-navy" : "border-white/12 bg-slate-800 text-white hover:border-brand-copper/70"
              } ${!slot.available ? "cursor-not-allowed text-white/28 line-through opacity-45" : ""}`}
            >
              {slot.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
